import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { API_URL } from '../api';
import { getAllQuestions } from '../models/questions.js';
import { getAllImages } from '../models/images.js';

const correctAnswerVF = {
  0: 'F',
  1: 'V',
};

// Base pública del backend: es la misma del API pero sin el sufijo `/api`,
// porque el endpoint público se monta fuera de las rutas autenticadas.
const PUBLIC_BASE = (API_URL || '').replace(/\/api\/?$/, '');

// Enlace permanente a la imagen: apunta al endpoint público, que firma una URL
// fresca de S3 en el momento y redirige. Así el enlace nunca expira.
const buildImageLink = imageId => `${PUBLIC_BASE}/public/images/${imageId}/view`;

// Devuelve la respuesta correcta en formato legible según el tipo de pregunta.
const formatCorrectAnswer = question => {
  const answers = Array.isArray(question.possible_answers) ? question.possible_answers : [];
  if (question.question_type === 'ALTERNATIVAS') {
    return answers[question.correct_answer] ?? '';
  }
  if (question.question_type === 'V/F') {
    return correctAnswerVF[question.correct_answer] ?? '';
  }
  return answers.join(' | ');
};

const formatLearnUnits = learnUnits => {
  const units = Array.isArray(learnUnits) ? learnUnits : [];
  return units.map(unit => unit.name).join(', ');
};

// Descarga todas las preguntas en un archivo Excel. La columna "Imagen" muestra
// un enlace "Ver imagen" (o "Sin imagen" cuando no tiene) que redirige a la
// imagen asociada.
export const exportQuestionsToExcel = async () => {
  const [questions, images] = await Promise.all([getAllQuestions(), getAllImages()]);

  // Conjunto de ids de imágenes existentes, para no generar enlaces a imágenes
  // que ya no están.
  const existingImageIds = new Set((images || []).map(image => image?.id).filter(Boolean));

  const rows = [];
  // Enlace por fila (o null si no tiene imagen), en el mismo orden que `rows`.
  const imageLinks = [];

  (questions || []).forEach(question => {
    const hasImage = question.image_id && existingImageIds.has(question.image_id);
    rows.push({
      ID: question.id,
      Pregunta: question.content,
      Tipo: question.question_type,
      Alternativas: Array.isArray(question.possible_answers)
        ? question.possible_answers.join(' | ')
        : '',
      'Respuesta correcta': formatCorrectAnswer(question),
      'Tiempo (seg.)': question.time_to_answer,
      'Unidad(es) de aprendizaje': formatLearnUnits(question.learnUnits),
      Imagen: hasImage ? 'Ver imagen' : 'Sin imagen',
    });
    imageLinks.push(hasImage ? buildImageLink(question.image_id) : null);
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);

  // Convertir las celdas de la columna "Imagen" que tienen imagen en hipervínculos.
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  let imagenCol = -1;
  for (let c = range.s.c; c <= range.e.c; c++) {
    const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c })];
    if (headerCell && headerCell.v === 'Imagen') {
      imagenCol = c;
      break;
    }
  }
  if (imagenCol >= 0) {
    imageLinks.forEach((link, i) => {
      if (!link) return;
      // Los datos empiezan en la fila 1 (la 0 es el encabezado).
      const cellRef = XLSX.utils.encode_cell({ r: i + 1, c: imagenCol });
      const cell = worksheet[cellRef];
      if (cell) {
        cell.l = { Target: link, Tooltip: 'Abrir imagen' };
      }
    });
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Preguntas');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, 'preguntas.xlsx');
};
